import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  useTheme,
  alpha,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Breadcrumbs,
  styled,
  Stack,
  ListItem,
  ListItemIcon,
  ListItemText,
  List,
  StepConnector,
  stepConnectorClasses,
  ListItemAvatar,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Timeline as TimelineIcon,
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  MoreVert as MoreVertIcon,
  History as HistoryIcon,
  Assignment as AssignmentIcon,
  FormatListBulleted as FormatListBulletedIcon,
  Done as DoneIcon,
  ArrowForward as ArrowForwardIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  AttachFile as AttachFileIcon,
  Cached as CachedIcon,
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
  FileOpen as FileOpenIcon,
  Close as CloseIcon,
  Info as InfoIcon
} from '@mui/icons-material';

import workflowService from '../../../services/workflowService';

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper to get file icon based on file type
const getFileIcon = (fileName) => {
  if (!fileName) return <FileIcon />;
  
  const extension = fileName.split('.').pop().toLowerCase();
  
  if (['pdf'].includes(extension)) {
    return <PdfIcon color="error" />;
  } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
    return <ImageIcon color="primary" />;
  } else {
    return <FileIcon color="action" />;
  }
};

// Helper to format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const StyledStepConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300],
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const StyledStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
  display: 'flex',
  height: 22,
  alignItems: 'center',
  ...(ownerState.active && {
    color: theme.palette.primary.main,
  }),
  '& .StepIcon-completedIcon': {
    color: theme.palette.primary.main,
    zIndex: 1,
    fontSize: 18,
  },
  '& .StepIcon-circle': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
  },
}));

function StyledStepIcon(props) {
  const { active, completed, className } = props;

  return (
    <StyledStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <DoneIcon className="StepIcon-completedIcon" />
      ) : (
        <div className="StepIcon-circle" />
      )}
    </StyledStepIconRoot>
  );
}

const AdminWorkflowDetailPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const { user } = useAuth();
  
  // Determine if we're in staff context
  const isStaffPortal = location.pathname.startsWith('/staff/') || user?.role === 'staff';
  
  // Update navigation to respect the user's role context
  const getBackUrl = () => isStaffPortal ? '/staff/workflows' : '/admin/workflows';

  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  
  // Action menu and dialogs
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionComment, setActionComment] = useState('');
  
  // File preview state
  const [previewFile, setPreviewFile] = useState(null);
  
  // Add SLA display toggle
  const [showSLA, setShowSLA] = useState(false);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        // Use the appropriate function based on user role
        let response;
        if (isStaffPortal) {
          // Check if there's a staff-specific endpoint
          response = await workflowService.getStaffRequestById(requestId);
        } else {
          response = await workflowService.getAdminRequestById(requestId);
        }
        
        setRequest(response.data);
        
        // Find the current step index to set the active step
        if (response.data.workflowTemplate && response.data.workflowTemplate.steps && response.data.currentStep) {
          const currentStepIndex = response.data.workflowTemplate.steps.findIndex(
            step => step._id === response.data.currentStep._id
          );
          if (currentStepIndex !== -1) {
            setActiveStep(currentStepIndex);
          }
        }
      } catch (err) {
        console.error('Error fetching workflow request:', err);
        setError('Failed to load workflow request details.');
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchRequest();
    }
  }, [requestId, isStaffPortal]);

  // Add this useEffect to keep activeStep in sync with request data
  useEffect(() => {
    if (request && request.workflowTemplate && request.workflowTemplate.steps && request.currentStep) {
      // Find the current step index in the steps array
      const currentStepIndex = request.workflowTemplate.steps.findIndex(
        step => {
          // Try multiple ways to match the currentStep
          if (typeof request.currentStep === 'string') {
            return step._id === request.currentStep;
          } else if (request.currentStep._id) {
            return step._id === request.currentStep._id;
          }
          return false;
        }
      );
      
      // If found a valid index, update the activeStep state
      if (currentStepIndex !== -1 && currentStepIndex !== activeStep) {
        console.log(`Updating active step from ${activeStep} to ${currentStepIndex} based on request data`);
        setActiveStep(currentStepIndex);
      }
    }
  }, [request, activeStep]);

  const handleActionsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleActionsClose = () => {
    setAnchorEl(null);
  };

  const handleActionSelect = (action) => {
    setSelectedAction(action);
    setActionComment('');
    setActionDialogOpen(true);
    handleActionsClose();
  };

  const handleActionDialogClose = () => {
    setActionDialogOpen(false);
    setSelectedAction(null);
  };

  const handlePerformAction = async () => {
    if (!selectedAction) return;

    setProcessing(true);
    setIsSubmitting(true);

    try {
      // Log what action we're performing for debugging
      console.log(`Performing action: ${selectedAction.id}, type: ${selectedAction.type}`);
      
      // Set optimistic UI update for better user experience
      if (selectedAction.nextStepId) {
        const nextStepIndex = request.workflowTemplate.steps.findIndex(
          step => step._id === selectedAction.nextStepId || String(step.order) === String(selectedAction.nextStepId)
        );
        
        if (nextStepIndex !== -1) {
          console.log(`Setting optimistic step update to: ${nextStepIndex}`);
          setActiveStep(nextStepIndex);
        }
      }
      
      const response = await workflowService.performAction(
        requestId,
        selectedAction.id,
        actionComment
      );

      // Properly handle the response data
      if (response && response.data) {
        console.log('Action response data:', response.data);
        
        // Update the request state with the latest data
        setRequest(response.data);
        
        // Update active step based on the new state
        const updatedRequest = response.data;
        if (updatedRequest.workflowTemplate && updatedRequest.workflowTemplate.steps && updatedRequest.currentStep) {
          const currentStepIndex = updatedRequest.workflowTemplate.steps.findIndex(
            step => {
              if (typeof updatedRequest.currentStep === 'string') {
                return step._id === updatedRequest.currentStep;
              } else if (updatedRequest.currentStep._id) {
                return step._id === updatedRequest.currentStep._id;
              }
              return false;
            }
          );
          
          if (currentStepIndex !== -1) {
            console.log(`Updating active step to: ${currentStepIndex} based on response`);
            setActiveStep(currentStepIndex);
          }
        }
      }
      
      // Close dialog and set success message
      setActionDialogOpen(false);
      setSuccessMessage(`Action "${selectedAction.label}" performed successfully.`);
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      console.error('Error performing action:', err);
      setError(`Failed to perform action: ${err.message || 'Unknown error'}`);
    } finally {
      setProcessing(false);
      setIsSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await workflowService.addComment(requestId, comment);
      setRequest(response.data);
      setComment('');
      setSuccessMessage('Comment added successfully.');
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      console.error('Error adding comment:', err);
      setError(`Failed to add comment: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMoveToNextStep = async () => {
    if (!request || !request.workflowTemplate || !request.workflowTemplate.steps) {
      setError('Workflow steps are not available');
      return;
    }    

    // Use the activeStep state instead of trying to find it again
    if (activeStep >= request.workflowTemplate.steps.length - 1) {
      setError('Already at the last step');
      return; // Already at the last step
    }    

    const nextStep = request.workflowTemplate.steps[activeStep + 1];
    const currentStepData = request.workflowTemplate.steps[activeStep];

    // Set consistent processing state for visual feedback
    setProcessing(true);
    
    try {
      // Log detailed information about what we're working with
      console.log('Current step data:', currentStepData);
      console.log('Next step data:', nextStep);
      console.log('Current step actions:', currentStepData.actions);
      
      // Check for available actions in the enhanced response structure
      let adminActions = [];
      
      // First check if we have the enhanced availableActions property from our API improvements
      if (request.availableActions && request.availableActions.admin) {
        adminActions = request.availableActions.admin;
        console.log('Using enhanced availableActions.admin:', adminActions);
      } else {    
        // Fall back to the original filtering approach
        adminActions = currentStepData.actions.filter(action => 
          action.performer === 'admin' || action.performer === 'both'
        );
        console.log('Using filtered admin actions:', adminActions);
      }    
      
      if (adminActions.length === 0) {
        setError('No admin actions available for this step');
        setProcessing(false);
        return;
      }
      
      // Try different strategies to find an appropriate action for moving to the next step
      let moveAction = null;
      
      // 1. First try to find an "approve" type action
      moveAction = adminActions.find(action => action.type === 'approve');
      console.log('Approve action found:', moveAction);
      
      // 2. If no approve action, look for any action with matching nextStepId
      if (!moveAction) {
        // Try matching by order, step id string, or direct comparison
        moveAction = adminActions.find(action => {
          if (!action.nextStepId) return false;
          
          // Check if nextStepId equals step order (as string)
          if (action.nextStepId === String(nextStep.order)) return true;
          
          // Check if nextStepId equals step._id
          if (action.nextStepId === nextStep._id) return true;
          
          // Check if nextStepId equals step._id as string
          if (nextStep._id && action.nextStepId === nextStep._id.toString()) return true;
          
          return false;
        });
        console.log('Action with matching nextStepId:', moveAction);
      }
      
      // 3. Last resort: use the first admin action that's not "reject" type
      if (!moveAction) {
        moveAction = adminActions.find(action => action.type !== 'reject');
        console.log('Using first non-reject action:', moveAction);
      }
      
      // 4. If still no action, use the very first admin action
      if (!moveAction && adminActions.length > 0) {
        moveAction = adminActions[0];
        console.log('Using first available action as fallback:', moveAction);
      }
      
      if (!moveAction) {
        setError('No suitable action found to move to the next step.');
        setProcessing(false);
        return;
      }
      
      console.log('Using action to move to next step:', moveAction);
      
      // Create an optimistic update for immediate UI feedback
      const optimisticStepUpdate = activeStep + 1;
      setActiveStep(optimisticStepUpdate);
      
      const response = await workflowService.performAction(
        requestId,
        moveAction.id,
        `Admin moved the request to the next step: ${nextStep.name}`
      );
      
      console.log('Action response:', response);
      
      // Update the request data with the response - CRITICAL FIX
      if (response && response.data) {
        const updatedRequest = response.data;
        
        // Find the new active step in the response data
        if (updatedRequest.workflowTemplate && updatedRequest.workflowTemplate.steps && updatedRequest.currentStep) {
          const newStepIndex = updatedRequest.workflowTemplate.steps.findIndex(
            step => {
              if (typeof updatedRequest.currentStep === 'string') {
                return step._id === updatedRequest.currentStep;
              } else if (updatedRequest.currentStep._id) {
                return step._id === updatedRequest.currentStep._id;
              }
              return false;
            }
          );
          
          if (newStepIndex !== -1) {
            console.log(`Updating active step to: ${newStepIndex} based on response data`);
            setActiveStep(newStepIndex);
          }
        }
        
        // Update the full request state
        setRequest(updatedRequest);
      }
      
      setSuccessMessage(`Workflow moved to the next step: ${nextStep.name}`);
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      console.error('Error moving to next step:', err);
      setError(`Failed to move to next step: ${err.message || 'Unknown error'}`);
      
      // Revert the optimistic update if there's an error
      if (optimisticStepUpdate) {
        setActiveStep(activeStep);
      }
    } finally {
      setProcessing(false);
    }
  };

  const refreshWorkflowData = async () => {
    try {
      setLoading(true);
      
      // Use the appropriate function based on user role
      let response;
      if (isStaffPortal) {
        response = await workflowService.getStaffRequestById(requestId);
      } else {
        response = await workflowService.getAdminRequestById(requestId);
      }
      
      // Update the request state
      setRequest(response.data);
      
      // Find the current step index
      if (response.data?.workflowTemplate?.steps && response.data?.currentStep) {
        const currentStepIndex = response.data.workflowTemplate.steps.findIndex(
          step => step._id === response.data.currentStep._id
        );
        
        if (currentStepIndex !== -1) {
          console.log(`Setting active step to: ${currentStepIndex} after refresh`);
          setActiveStep(currentStepIndex);
        }
      }
    } catch (err) {
      console.error('Error refreshing workflow data:', err);
      setError('Failed to refresh workflow data');
    } finally {
      setLoading(false);
    }
  };

  const renderAvailableActions = () => {
    // Check if we have the enhanced action structure from our API improvements
    let availableActions = [];
    
    if (request?.availableActions?.admin) {
      // Use the enhanced structure if available
      availableActions = request.availableActions.admin;
      console.log('Rendering actions from enhanced availableActions.admin:', availableActions);
    } else if (request?.workflowTemplate?.steps && activeStep >= 0 && activeStep < request.workflowTemplate.steps.length) {
      // Fall back to filtering the actions from the current step
      const currentStepActions = request.workflowTemplate.steps[activeStep].actions || [];
      availableActions = currentStepActions.filter(
        action => action.performer === 'admin' || action.performer === 'both'
      );
      console.log('Rendering actions from filtered step actions:', availableActions);
    } else if (request?.currentStep?.actions && request.currentStep.actions.length > 0) {
      // Last resort: try to get actions from the currentStep property
      availableActions = request.currentStep.actions.filter(
        action => action.performer === 'admin' || action.performer === 'both'
      );
      console.log('Rendering actions from currentStep.actions:', availableActions);
    }

    if (availableActions.length === 0) {
      console.log('No admin actions available for this step');
      return null;
    }

    // Check which actions have been executed already by looking at the history
    const executedActionIds = request.history
      ? request.history
          .filter(item => item.data && item.data.actionId)
          .map(item => item.data.actionId)
      : [];

    console.log('Actions already executed:', executedActionIds);

    return (
      <>
        <Button 
          variant="contained"
          onClick={handleActionsClick}
          endIcon={<MoreVertIcon />}
          sx={{ borderRadius: 1 }}
        >
          Actions
        </Button>
        <Menu 
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleActionsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {availableActions.map((action) => {
            // Check if this action has been executed already
            const isExecuted = executedActionIds.includes(action.id);
            
            return (
              <MenuItem 
                key={action.id} 
                onClick={() => handleActionSelect(action)}
                sx={{ 
                  minWidth: 150,
                  gap: 1,
                  py: 1.5,
                  // Apply different styles for executed actions
                  ...(isExecuted && {
                    bgcolor: alpha(theme.palette.grey[300], 0.5),
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.grey[400], 0.5),
                    }
                  })
                }}
              >
                {action.type === 'approve' && (
                  isExecuted ? 
                    <DoneIcon fontSize="small" color="success" /> : 
                    <CheckCircleIcon fontSize="small" color="success" />
                )}
                {action.type === 'reject' && (
                  isExecuted ? 
                    <DoneIcon fontSize="small" color="error" /> : 
                    <CancelIcon fontSize="small" color="error" />
                )}
                {!['approve', 'reject'].includes(action.type) && (
                  isExecuted ? 
                    <DoneIcon fontSize="small" color="disabled" /> : 
                    <DoneIcon fontSize="small" color="primary" />
                )}
                {isExecuted ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {action.label}
                    <Chip 
                      label="Executed" 
                      size="small" 
                      color="default"
                      variant="outlined"
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                ) : (
                  action.label
                )}
              </MenuItem>
            );
          })}
        </Menu>
        <Dialog 
          open={actionDialogOpen} 
          onClose={handleActionDialogClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {selectedAction?.label || 'Perform Action'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {selectedAction?.description || 'Are you sure you want to perform this action?'}
            </DialogContentText>
            {/* Check if action has been executed before */}
            {selectedAction && executedActionIds.includes(selectedAction.id) && (
              <Alert 
                severity="warning" 
                sx={{ mt: 2, mb: 1 }}
                icon={<InfoIcon />}
              >
                This action has already been executed. Performing it again may cause unexpected results.
              </Alert>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Comment (optional)"
              fullWidth
              multiline
              rows={4}
              value={actionComment}
              onChange={(e) => setActionComment(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleActionDialogClose} disabled={processing}>Cancel</Button>
            <LoadingButton 
              onClick={handlePerformAction} 
              loading={processing}
              variant="contained"
              color="primary"
            >
              Confirm
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  // Add new handler for file preview
  const handleFilePreview = (attachment) => {
    const fileUrl = workflowService.getAttachmentUrl(attachment.filename);
    setPreviewFile({
      name: attachment.originalName || attachment.filename,
      url: fileUrl,
      type: attachment.mimetype
    });
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(getBackUrl())}
        >
          Back to Workflows
        </Button>
      </Container>
    );
  }

  if (!request) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>Workflow request not found</Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(getBackUrl())}
        >
          Back to Workflows
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
            borderRadius: 1 
          }}
        >
          {successMessage}
        </Alert>
      )}
      
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
        <Link 
          to="/admin/dashboard" 
          style={{ 
            textDecoration: 'none', 
            color: theme.palette.text.secondary,
            display: 'flex',
            alignItems: 'center' 
          }}
        >
          <TimelineIcon fontSize="small" sx={{ mr: 0.5 }} />
          Dashboard
        </Link>
        <Link 
          to="/admin/workflows" 
          style={{ 
            textDecoration: 'none', 
            color: theme.palette.text.secondary,
            display: 'flex',
            alignItems: 'center' 
          }}
        >
          <TimelineIcon fontSize="small" sx={{ mr: 0.5 }} />
          Workflows
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <AssignmentIcon fontSize="small" sx={{ mr: 0.5 }} />
          Request Details
        </Typography>
      </Breadcrumbs>
      
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 1
      }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {request.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Request ID: {request._id} • Created: {formatDate(request.createdAt)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(getBackUrl())}
            sx={{ borderRadius: 1 }}
          >
            Back to Workflows
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<CachedIcon />}
            onClick={refreshWorkflowData}
            disabled={loading}
            sx={{ borderRadius: 1 }}
          >
            Refresh
          </Button>
          {renderAvailableActions()}
        </Box>
      </Box>
      
      {/* Add Stepper for Workflow Steps */}
      {request?.workflowTemplate?.steps && request.workflowTemplate.steps.length > 0 && (
        <Paper 
          elevation={0}
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Workflow Progress
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showSLA}
                  onChange={(e) => setShowSLA(e.target.checked)}
                  color="primary"
                />
              }
              label="Show SLA Limits"
            />
          </Box>
          <Stepper 
            activeStep={activeStep} 
            alternativeLabel 
            connector={<StyledStepConnector />}
            sx={{ mb: 2 }}
          >
            {request.workflowTemplate.steps.map((step, index) => (
              <Step key={step._id || index}>
                <StepLabel 
                  StepIconComponent={StyledStepIcon}
                  sx={{
                    '& .MuiStepLabel-label': {
                      mt: 1,
                      fontWeight: index === activeStep ? 600 : 400,
                      color: index === activeStep ? 'primary.main' : 'text.secondary',
                    }
                  }}
                >
                  <Box>
                    {step.name}
                    {showSLA && step.slaHours && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        SLA: {step.slaHours} hours
                      </Typography>
                    )}
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {/* Add debug info to help troubleshoot timeline issues */}
          {process.env.NODE_ENV !== 'production' && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
              <Typography variant="caption" display="block">
                Debug - Current Step: {request.currentStep?._id || request.currentStep || 'None'} 
              </Typography>
              <Typography variant="caption" display="block">
                Active Step Index: {activeStep}
              </Typography>
            </Box>
          )}
          
          {/* Add button to move to next step */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              disabled={
                processing || 
                !request.workflowTemplate.steps || 
                activeStep >= request.workflowTemplate.steps.length - 1
              }
              onClick={handleMoveToNextStep}
              startIcon={<ArrowForwardIcon />}
            >
              {processing ? 'Processing...' : 'Move to Next Step'}
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Request Info */}
        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <CardHeader 
              title="Request Information" 
              sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
            />
            <CardContent>
              <Stack spacing={2}>
                {/* Status */}
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 1, 
                  bgcolor: alpha(theme.palette.primary.main, 0.07),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Avatar sx={{ 
                    bgcolor: request.isCompleted 
                      ? theme.palette.success.main 
                      : theme.palette.primary.main
                  }}>
                    {request.isCompleted ? <CheckCircleIcon /> : <AssignmentIcon />}
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Current Status
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {request.currentStatus}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Member Info */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Submitted By
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: theme.palette.secondary.main,
                        width: 36,
                        height: 36,
                        mr: 1.5
                      }}
                    >
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {request.member?.userId?.firstName} {request.member?.userId?.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {request.member?.userId?.email}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
                
                {/* Workflow Type */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Workflow Template
                  </Typography>
                  <Typography variant="body1">
                    {request.workflowTemplate?.name || 'Custom Workflow'}
                  </Typography>
                </Paper>
                
                {/* Current Step */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Current Step
                  </Typography>
                  <Chip 
                    label={
                      request.workflowTemplate?.steps && 
                      activeStep >= 0 && 
                      activeStep < request.workflowTemplate.steps.length
                        ? request.workflowTemplate.steps[activeStep].name
                        : request.currentStep?.name || 'Not Available'
                    }
                    color="primary"
                    sx={{ mt: 0.5 }}
                  />
                </Paper>
                
                {/* Timestamps */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Timeline
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Submitted
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(request.createdAt)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Last Updated
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(request.updatedAt)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Stack>
            </CardContent>
          </Card>
          
          {/* Form Data */}
          {request.formData && Object.keys(request.formData).length > 0 && (
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
              <CardHeader 
                title="Form Information" 
                sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
              />
              <CardContent>
                <List disablePadding>
                  {Object.entries(request.formData).map(([key, value]) => (
                    <ListItem 
                      key={key} 
                      divider
                      sx={{ 
                        px: 2, 
                        py: 1.5,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                      }}
                    >
                      <ListItemText
                        primary={<Typography variant="caption" color="text.secondary">{key}</Typography>}
                        secondary={
                          value !== null && value !== undefined ? (
                            Array.isArray(value) ? value.join(', ') : String(value)
                          ) : (
                            'Not provided'
                          )
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Grid>
        
        {/* Right Column - Comments & History */}
        <Grid item xs={12} md={7}>
          {/* Activity History */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <CardHeader 
              title="Activity Timeline" 
              sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
              avatar={<HistoryIcon color="primary" />}
            />
            <CardContent sx={{ px: 2, py: 1 }}>
              {request.history && request.history.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  {request.history.map((item, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex' }}>
                        <Box sx={{ mr: 2 }}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: item.actor === 'admin' 
                                ? theme.palette.primary.main 
                                : item.actor === 'member'
                                ? theme.palette.secondary.main
                                : theme.palette.grey[500]
                            }}
                          >
                            {item.actor === 'admin' ? 'A' : item.actor === 'member' ? 'M' : 'S'}
                          </Avatar>
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2">
                              {item.actor === 'admin' ? 'Administrator' : item.actor === 'member' ? 'Member' : 'System'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(item.timestamp)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Action: <strong>{item.action}</strong>
                          </Typography>
                          <Typography variant="body2">
                            {item.description}
                          </Typography>
                        </Box>
                      </Box>
                      {index < request.history.length - 1 && <StyledStepConnector />}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No activity history available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Comments Section */}
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <CardHeader 
              title="Comments" 
              sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
              avatar={<CommentIcon color="primary" />}
            />
            <CardContent>
              <Box sx={{ mb: 4 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Add a comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={isSubmitting}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <LoadingButton
                    variant="contained"
                    onClick={handleAddComment}
                    disabled={!comment.trim() || isSubmitting}
                    loading={isSubmitting}
                    startIcon={<SendIcon />}
                  >
                    Add Comment
                  </LoadingButton>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              {request.comments && request.comments.length > 0 ? (
                <Stack spacing={2}>
                  {request.comments.map((comment, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: comment.by === 'admin' ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                        borderColor: comment.by === 'admin' ? alpha(theme.palette.primary.main, 0.3) : theme.palette.divider
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: comment.by === 'admin' ? theme.palette.primary.main : theme.palette.secondary.main,
                              mr: 1
                            }}
                          >
                            {comment.by === 'admin' ? 'A' : 'M'}
                          </Avatar>
                          <Typography variant="subtitle2">
                            {comment.by === 'admin' ? 'Administrator' : 'Member'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(comment.date)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1, pl: 5 }}>
                        {comment.text}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CommentIcon color="disabled" sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                  <Typography variant="body2" color="text.secondary">
                    No comments yet
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Attachments Section - Enhanced section with preview */}
          {request.attachments && request.attachments.length > 0 && (
            <Card sx={{ mt: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
              <CardHeader 
                title="Attachments" 
                sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
                avatar={<AttachFileIcon color="primary" />}
              />
              <CardContent>
                <List>
                  {request.attachments.map((attachment, index) => (
                    <ListItem 
                      key={index} 
                      divider={index < request.attachments.length - 1}
                      sx={{ py: 1.5 }}
                    >
                      <ListItemAvatar>
                        <Avatar 
                          variant="rounded" 
                          sx={{ 
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            cursor: 'pointer' 
                          }}
                          onClick={() => handleFilePreview(attachment)}
                        >
                          {getFileIcon(attachment.originalName)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography 
                            variant="body2" 
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleFilePreview(attachment)}
                          >
                            {attachment.originalName}
                          </Typography>
                        }
                        secondary={formatFileSize(attachment.size)}
                      />
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="Preview">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleFilePreview(attachment)}
                            sx={{ mr: 1 }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            component="a"
                            href={workflowService.getAttachmentUrl(attachment.filename)}
                            target="_blank"
                            download={attachment.originalName}
                          >
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {/* File Preview Dialog */}
          <Dialog
            open={!!previewFile}
            onClose={handleClosePreview}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: { borderRadius: 2 }
            }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" noWrap sx={{ maxWidth: '80%' }}>
                {previewFile?.name}
              </Typography>
              <Box>
                <IconButton 
                  edge="end" 
                  aria-label="download" 
                  href={previewFile?.url}
                  download={previewFile?.name}
                  component="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{ mr: 1 }}
                  title="Download"
                >
                  <GetAppIcon />
                </IconButton>
                <IconButton 
                  edge="end" 
                  aria-label="open" 
                  href={previewFile?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  component="a"
                  size="small"
                  sx={{ mr: 1 }}
                  title="Open in New Tab"
                >
                  <FileOpenIcon />
                </IconButton>
                <IconButton 
                  edge="end" 
                  aria-label="close" 
                  onClick={handleClosePreview}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, height: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {previewFile && (
                previewFile.type?.startsWith('image/') ? (
                  <img 
                    src={previewFile.url} 
                    alt={previewFile.name} 
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                  />
                ) : previewFile.type === 'application/pdf' ? (
                  <iframe 
                    src={previewFile.url} 
                    width="100%" 
                    height="100%" 
                    title={previewFile.name}
                    style={{ border: 'none' }}
                  />
                ) : (
                  <Box sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Box sx={{ mb: 2 }}>
                      {getFileIcon(previewFile.name)}
                    </Box>
                    <Typography variant="body1" gutterBottom>
                      Preview not available for this file type
                    </Typography>
                    <Button 
                      variant="contained" 
                      href={previewFile.url}
                      download={previewFile.name}
                      component="a"
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<GetAppIcon />}
                      sx={{ mt: 2 }}
                    >
                      Download File
                    </Button>
                  </Box>
                )
              )}
            </DialogContent>
          </Dialog>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdminWorkflowDetailPage;